import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouterModule} from '@angular/router';
import { CommonModule, registerLocaleData } from '@angular/common';
import {LOCALE_ID, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {HttpModule} from '@angular/http';
import {HttpClientModule, HttpClient, HTTP_INTERCEPTORS} from '@angular/common/http';

import {TranslateModule, TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';

import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';

import {AgmCoreModule} from '@agm/core';

import {
    MatSidenavModule,
    MatCardModule,
    MatMenuModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatTabsModule,
    MatListModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    MatDialogModule, MatInputModule
} from '@angular/material';

import {FlexLayoutModule} from '@angular/flex-layout';
import {BidiModule} from '@angular/cdk/bidi';

import {AppRoutes} from './app.routing';
import {AppComponent} from './app.component';
import {AdminLayoutComponent} from './layouts/admin/admin-layout.component';
import {AuthLayoutComponent} from './layouts/auth/auth-layout.component';
import {SharedModule} from './shared/shared.module';


// custom providers
import {MessageProvider} from "./shared/providers/message.provider";
import {ErrorMessageProvider} from "./shared/providers/error.message.provider";
import {AuthService} from './shared/services/auth.service';
import {AuthGuard} from './shared/services/auth.guard';
import {GuestGuard} from './shared/services/guest.guard';
import {CompanyService} from "./shared/services/company.service";
import {BranchService} from "./shared/services/branch.service";
import {CatalogService} from "./shared/services/catalog.service";

//import {DialogConfirmComponent} from "./shared/components/dialog.confirm";
import {CatalogSampleService} from "./shared/services/catalog.sample.service";
import {CatalogCategoryService} from "./shared/services/catalog.category.service";
import {CatalogPrerequisiteService} from "./shared/services/catalog.prerequisite.service";
import {UserService} from "./shared/services/user.service";
import {InventoryService} from "./shared/services/inventory.service";
import {AdminGuard} from "./shared/services/admin.guard";
import {EmployeeService} from "./shared/services/employee.service";
import {UserRolService} from "./shared/services/user.rol.service";
import {EmployeeRolService} from "./shared/services/employee.rol.service";
import {DiscountService} from "./shared/services/discount.service";
import {LabService} from "./shared/services/lab.service";
import {PatientService} from "./shared/services/patient.service";
import {CivilStatusService} from "./shared/services/civil.status.service";
import {GenderService} from "./shared/services/gender.service";
import {ExternalDoctorService} from "./shared/services/external.doctor.service";
import {ExternalCompanyService} from "./shared/services/external.company.service";
import {ProfileService} from "./shared/services/profile.service";
import {DialogConfirmComponent} from "./shared/modules/dialog.confirm";
import {LockscreenComponent} from "./session/lockscreen/lockscreen.component";
import {LockGuard} from "./shared/services/lock.guard";
import {WorkService} from "./shared/services/work.service";
import {StudyService} from "./shared/services/study.service";
import {StudyStatusService} from "./shared/services/study.status.service";
import {WorkStatusService} from "./shared/services/work.status.service";
import {RequestsInterceptor} from "./shared/services/requests.interceptor";
import {CatalogResultService} from "./shared/services/catalog.result.service";
import {CatalogResultGroupService} from "./shared/services/catalog.result.group.service";
import {ExternalCompanyPaymentService} from "./shared/services/external.company.payment.service";
import {AgeRangeService} from "./shared/services/age.range.service";
import {PasswordConfirmationComponent} from "./shared/modules/password.confirmation";

// Es necesario decirle a Angular que "hable español"
// continua linea 205
import localeEsMx from "@angular/common/locales/es-MX";
registerLocaleData(localeEsMx, 'es-MX');

export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    suppressScrollX: true,
    wheelSpeed: 2,
    wheelPropagation: true,
    minScrollbarLength: 20
};

@NgModule({
    declarations: [
        AppComponent,
        AdminLayoutComponent,
        AuthLayoutComponent,
        DialogConfirmComponent,
        PasswordConfirmationComponent,
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        SharedModule,
        RouterModule.forRoot(AppRoutes),
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient]
            }
        }),
        MatSidenavModule,
        MatCardModule,
        MatMenuModule,
        MatCheckboxModule,
        MatIconModule,
        MatButtonModule,
        MatInputModule,
        MatToolbarModule,
        MatTabsModule,
        MatListModule,
        MatSlideToggleModule,
        MatSelectModule,
        MatProgressBarModule,
        MatSnackBarModule,
        MatAutocompleteModule,
        MatDialogModule,
        FlexLayoutModule,

        BidiModule,
        AgmCoreModule.forRoot({apiKey: 'YOURAPIKEY'}),

        //custom imports
        HttpModule,
        PerfectScrollbarModule
    ],
    providers: [

        //for the token and http interceptor
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RequestsInterceptor,
            multi: true
        },

        //custom providers
        AuthGuard,
        AdminGuard,
        GuestGuard,
        AuthService,
        LockGuard,

        MessageProvider,
        ErrorMessageProvider,

        CompanyService,
        BranchService,
        CatalogService,
        CatalogSampleService,
        CatalogCategoryService,
        CatalogPrerequisiteService,
        UserService,
        UserRolService,
        InventoryService,
        EmployeeService,
        EmployeeRolService,
        DiscountService,
        LabService,
        PatientService,
        CivilStatusService,
        GenderService,
        ExternalDoctorService,
        ExternalCompanyService,
        ProfileService,
        WorkService,
        WorkStatusService,
        StudyService,
        StudyStatusService,
        CatalogResultService,
        CatalogResultGroupService,
        ExternalCompanyPaymentService,
        AgeRangeService,

        {
            provide: PERFECT_SCROLLBAR_CONFIG,
            useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
        },

        {
            provide: LOCALE_ID,
            useValue: "es-MX"
        },
    ],
    entryComponents: [
        DialogConfirmComponent,
        PasswordConfirmationComponent,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}



// WEBPACK FOOTER //
// /Users/cricketmon/Sites/clubsandwichadmin/src/app/app.module.ts